import { ReactNode } from 'react';
import {
  Select as AriaSelect,
  Label,
  Button as AriaButton,
  SelectValue,
  Popover,
  ListBox,
  ListBoxItem,
  type Key,
} from 'react-aria-components';
import { Check, ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export type SelectVariant = 'field' | 'pill';

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  variant?: SelectVariant;
  isRequired?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const triggerBase =
  `appearance-none group inline-flex items-center justify-between gap-2 ${typography.body} outline-none transition cursor-pointer ` +
  'data-[focus-visible]:ring-4 data-[focus-visible]:ring-[rgb(var(--c-ring)/0.24)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const fieldTrigger =
  'h-10 px-3 rounded-lg bg-surface border border-strong shadow-xs text-fg ' +
  'data-[open]:border-brand-300 data-[open]:ring-4 data-[open]:ring-[rgb(var(--c-ring)/0.24)] ' +
  'data-[invalid]:border-error-500';

const pillTrigger =
  `h-10 px-3.5 rounded-lg border border-strong bg-surface shadow-xs text-fg-secondary ${typography.label} ` +
  'hover:bg-muted-strong data-[selected-pill=true]:border-brand-solid/50 data-[selected-pill=true]:bg-brand-solid/12 data-[selected-pill=true]:text-brand-text';

const popover =
  'min-w-(--trigger-width) rounded-lg border border-default bg-surface p-1 shadow-lg outline-none ' +
  'entering:animate-in entering:fade-in-0 entering:zoom-in-95 exiting:animate-out exiting:fade-out-0';

const item =
  `flex cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2.5 py-2 ${typography.body} text-fg-secondary outline-none ` +
  'data-[focused]:bg-muted-strong data-[selected]:text-fg data-[selected]:font-medium';

/**
 * The Regask select — react-aria `Select` (keyboard + ARIA listbox). `field`
 * is the labeled form control matching TextInput; `pill` is the compact
 * toolbar filter that tints brand once a value is chosen. For long
 * type-to-find lists use SearchableSelect.
 */
export const Select = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  variant = 'field',
  isRequired,
  isInvalid,
  isDisabled,
  fullWidth,
  className,
}: SelectProps) => {
  const t = useT();
  const resolvedPlaceholder = placeholder ?? t('ui.select.placeholder');
  const isPill = variant === 'pill';
  const selected = options.find(o => o.value === value);
  const placeholderText = isPill
    ? (label ?? resolvedPlaceholder)
    : resolvedPlaceholder;
  return (
    <AriaSelect
      aria-label={isPill ? label : undefined}
      selectedKey={value || null}
      isRequired={isRequired}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      onSelectionChange={(key: Key | null) =>
        onChange(key === null ? '' : String(key))
      }
      className={cx('flex flex-col gap-1.5', fullWidth && 'w-full', className)}
    >
      {label && !isPill && (
        <Label className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {isRequired && <span className="ml-0.5 text-brand-text">*</span>}
        </Label>
      )}
      <AriaButton
        data-selected-pill={isPill && Boolean(value)}
        aria-label={isPill ? label : undefined}
        className={cx(
          triggerBase,
          isPill ? pillTrigger : fieldTrigger,
          fullWidth && 'w-full',
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected?.icon}
          {/* Render only the label text — the leading icon is drawn above from
              `selected.icon`, so letting SelectValue fall back to the item's
              full content would double the icon. */}
          <SelectValue className="truncate data-[placeholder]:text-fg-muted">
            {({ isPlaceholder, selectedText }) =>
              isPlaceholder ? placeholderText : selectedText
            }
          </SelectValue>
        </span>
        <Icon as={ChevronDown} size="sm" className="shrink-0 text-fg-muted" />
      </AriaButton>
      <Popover className={popover}>
        <ListBox className="max-h-80 overflow-auto outline-none">
          {options.map(option => (
            <ListBoxItem
              key={option.value}
              id={option.value}
              textValue={option.label}
              className={item}
            >
              {({ isSelected }) => (
                <>
                  <span className="flex items-center gap-2">
                    {option.icon}
                    {option.label}
                  </span>
                  {isSelected && (
                    <Icon
                      as={Check}
                      size="sm"
                      strokeWidth={2.5}
                      className="text-brand-text"
                    />
                  )}
                </>
              )}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
};
